/**
 * Provision Core Auth — flip one clinic's portal from Firebase to core auth,
 * or back, in the only order that does not strand impersonation links.
 *
 * TEMPORARY: remove with the Firebase -> core portal auth cutover.
 *
 * On Overview rather than the Portal Auth tab because it writes, and that tab
 * is read-only by design; and because step 2 is the Deploy section just below.
 *
 * Flip:     1. save env (mode + pasted token) → 2. deploy by hand → 3. mirror to metadata
 * Rollback: 1. metadata → firebase → 2. env → firebase → 3. deploy by hand
 *
 * Step 3 is never blocked — the operator decides the deploy has landed; the
 * card only warns when the live build looks older than the env change. The token field is
 * write-only: the server never returns it, and it is cleared once saved.
 */

import { useCallback, useEffect, useState } from 'react'
import { RefreshCw, Loader2 } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Badge, Input } from '../ui'
import { expressPortalsAPI } from '../../services/api'

function apiErrorMessage(err, fallback) {
  return err?.response?.data?.error?.message || fallback
}

function formatTime(iso) {
  return iso ? new Date(iso).toLocaleString() : '—'
}

const MODE_BADGE = {
  core: { variant: 'success', label: 'core' },
  firebase: { variant: 'secondary', label: 'firebase' },
  unknown: { variant: 'outline', label: 'unknown' },
}

function ModeBadge({ mode }) {
  if (mode === null || mode === undefined) return <Badge variant="outline">not set</Badge>
  const badge = MODE_BADGE[mode] || MODE_BADGE.unknown
  return <Badge variant={badge.variant}>{badge.label}</Badge>
}

/** A ✓ / ✗ / ? line. `ok === null` means the read failed. */
function CheckRow({ ok, label, detail }) {
  const icon = ok === true ? '✓' : ok === false ? '✗' : '?'
  const tone = ok === true ? 'text-green-600' : ok === false ? 'text-destructive' : 'text-muted-foreground'
  return (
    <div className="grid grid-cols-[1.5rem_11rem_1fr] gap-2 text-xs items-baseline">
      <span className={tone}>{icon}</span>
      <span className="text-muted-foreground">{label}</span>
      <span className="break-all">{detail}</span>
    </div>
  )
}

export default function ProvisionCoreAuthSection({ clinicId, onSaved, onDeployNow }) {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(null) // 'env-core' | 'metadata-core' | 'metadata-firebase' | 'env-firebase' | 'firebase-creation'
  const [actionError, setActionError] = useState(null)
  const [actionNotice, setActionNotice] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await expressPortalsAPI.getCoreAuthStatus(clinicId)
      setStatus(res.data.data)
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Unable to load core auth status'))
    } finally {
      setLoading(false)
    }
  }, [clinicId])

  useEffect(() => {
    setStatus(null)
    setToken('')
    setActionError(null)
    setActionNotice(null)
    load()
  }, [load])

  const run = async (key, request, notice) => {
    setBusy(key)
    setActionError(null)
    setActionNotice(null)
    try {
      await request()
      setActionNotice(notice)
      onSaved?.()
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Request failed'))
    } finally {
      setBusy(null)
      load()
    }
  }

  const handleSaveCoreEnv = () =>
    run(
      'env-core',
      async () => {
        await expressPortalsAPI.saveCoreAuthEnv(clinicId, 'core', token.trim())
        setToken('')
      },
      'Env saved to production and preview. Deploy the portal, then mirror the mode to metadata.'
    )

  const handleMirrorCore = () =>
    run(
      'metadata-core',
      () => expressPortalsAPI.mirrorCoreAuthMetadata(clinicId, 'core'),
      'Metadata set to core. The flip is complete.'
    )

  const handleRollbackMetadata = () =>
    run(
      'metadata-firebase',
      () => expressPortalsAPI.mirrorCoreAuthMetadata(clinicId, 'firebase'),
      'Metadata set to firebase. Now set the env back to firebase.'
    )

  const handleRollbackEnv = () =>
    run(
      'env-firebase',
      () => expressPortalsAPI.saveCoreAuthEnv(clinicId, 'firebase'),
      'Env set to firebase. Deploy the portal to finish the rollback.'
    )

  const handleFirebaseCreation = (value) => {
    const prompt =
      value === 'disabled'
        ? 'Stop creating Firebase accounts for new patients at this clinic?'
        : 'Start creating Firebase accounts for new patients at this clinic again?'
    if (!window.confirm(prompt)) return
    run(
      'firebase-creation',
      () => expressPortalsAPI.setFirebaseAccountCreation(clinicId, value),
      value === 'disabled' ? 'Firebase account creation is off.' : 'Firebase account creation is on.'
    )
  }

  const preconditions = status?.preconditions
  const mode = status?.mode
  const tokenOnBoth = status ? status.token.production === true && status.token.preview === true : false
  const envIsCore = mode?.targets?.production?.mode === 'core' && mode?.targets?.preview?.mode === 'core'
  const envIsFirebase = mode?.targets?.production?.mode === 'firebase' && mode?.targets?.preview?.mode === 'firebase'
  const needsDeploy = status?.deployment && !status.deployment.builtAfterEnvChange
  const flipped = envIsCore && mode?.metadataMode === 'core' && !needsDeploy

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              Provision Core Auth
              {flipped && <Badge variant="success">Live on core</Badge>}
              {mode?.metadataDrift && <Badge variant="warning">Metadata drift</Badge>}
              {mode?.drift && <Badge variant="warning">Production ≠ preview</Badge>}
            </CardTitle>
            <CardDescription>
              Switch this portal from Firebase to core auth. The env and the clinic metadata both carry the mode and
              must agree — the portal reads the env (after a deploy), core reads the metadata (immediately).
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3 w-3 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {loading && !status && (
          <div className="flex items-center text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 mr-2 animate-spin" />
            Reading Vercel and Core…
          </div>
        )}

        {loadError && <div className="text-xs text-destructive">{loadError}</div>}

        {status && (
          <>
            {status.errors?.length > 0 && (
              <div className="text-xs text-destructive">{status.errors.map((e) => e.message).join(' · ')}</div>
            )}

            <div className="space-y-1">
              <div className="text-xs font-medium">Preconditions</div>
              <CheckRow
                ok={preconditions.provisioned}
                label="Provisioned"
                detail={
                  preconditions.provisioned === false
                    ? 'features.portal_auth is not set — run provision_clinic.py for this clinic'
                    : 'features.portal_auth'
                }
              />
              <CheckRow
                ok={preconditions.magicLink.state === 'healthy' ? true : preconditions.magicLink.state === 'unavailable' ? null : false}
                label="Magic-link metadata"
                detail={
                  preconditions.magicLink.missingFields?.length
                    ? `Missing ${preconditions.magicLink.missingFields.join(', ')}`
                    : preconditions.magicLink.state
                }
              />
              <CheckRow
                ok={preconditions.redirectPrefixes ? true : null}
                label="Redirect prefixes"
                detail={preconditions.redirectPrefixes || 'Not set — only needed if the portal deep-links beyond /'}
              />
            </div>

            <div className="space-y-1">
              <div className="text-xs font-medium">Current state</div>
              <div className="grid grid-cols-[12.5rem_1fr] gap-2 text-xs items-center">
                <span className="text-muted-foreground">Env · production</span>
                <span><ModeBadge mode={mode.targets?.production?.mode} /></span>
                <span className="text-muted-foreground">Env · preview</span>
                <span><ModeBadge mode={mode.targets?.preview?.mode} /></span>
                <span className="text-muted-foreground">Metadata (core reads this)</span>
                <span><ModeBadge mode={mode.metadataMode} /></span>
                <span className="text-muted-foreground">Token</span>
                <span>
                  production {status.token.production ? 'set' : 'not set'} · preview{' '}
                  {status.token.preview ? 'set' : 'not set'}
                </span>
                <span className="text-muted-foreground">Env last changed</span>
                <span>{formatTime(status.envChangedAt)}</span>
                <span className="text-muted-foreground">Live production deploy</span>
                <span>
                  {formatTime(status.deployment?.activeCreatedAt)}
                  {needsDeploy && <span className="text-orange-600"> — older than the env change, redeploy required</span>}
                </span>
              </div>
            </div>

            <div className="space-y-3 border-t pt-3">
              <div className="text-xs font-medium">Flip to core</div>

              <div className="space-y-2">
                <div className="text-xs">
                  <span className="font-medium">1. Save env.</span>{' '}
                  <span className="text-muted-foreground">
                    Sets PORTAL_AUTH_MODE=core and PORTAL_AUTH_API_TOKEN on production and preview. The token is
                    stored as sensitive and cannot be read back.
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <Input
                    type="password"
                    autoComplete="off"
                    value={token}
                    onChange={(event) => setToken(event.target.value)}
                    placeholder={tokenOnBoth ? 'Token already set — leave blank to keep it' : 'Paste the portal_auth token'}
                    className="max-w-md h-8 text-xs"
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveCoreEnv}
                    disabled={!status.canSaveCore || busy !== null || (!token.trim() && !tokenOnBoth)}
                  >
                    {busy === 'env-core' && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                    Save env
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs">
                  <span className="font-medium">2. Deploy the portal.</span>{' '}
                  <span className="text-muted-foreground">The env only takes effect in a new build.</span>
                </div>
                <Button variant={needsDeploy ? 'default' : 'outline'} size="sm" onClick={onDeployNow}>
                  Go to Deploy
                </Button>
              </div>

              <div className="space-y-2">
                <div className="text-xs">
                  <span className="font-medium">3. Mirror to metadata.</span>{' '}
                  <span className="text-muted-foreground">
                    Sets metadata PORTAL_AUTH_MODE=core so core issues core-auth impersonation links. Do this after the
                    deploy is live.
                  </span>
                </div>
                {needsDeploy && (
                  <div className="text-xs text-orange-600">
                    The live production deploy looks older than the env change — make sure the new build is live first.
                  </div>
                )}
                {!status.envIsCore && (
                  <div className="text-xs text-orange-600">
                    Env is not core on both production and preview yet.
                  </div>
                )}
                <Button size="sm" onClick={handleMirrorCore} disabled={busy !== null}>
                  {busy === 'metadata-core' && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                  Mirror to metadata
                </Button>
              </div>
            </div>

            <div className="space-y-2 border-t pt-3">
              <div className="flex items-center gap-2 text-xs font-medium">
                Firebase accounts
                {status.firebaseAccountCreation === 'disabled' ? (
                  <Badge variant="secondary">Off</Badge>
                ) : status.firebaseAccountCreation === 'enabled' ? (
                  <Badge variant="success">On</Badge>
                ) : (
                  <Badge variant="outline">unknown</Badge>
                )}
              </div>
              {status.firebaseAccountCreation === 'disabled' ? (
                <Button variant="outline" size="sm" onClick={() => handleFirebaseCreation('enabled')} disabled={busy !== null}>
                  {busy === 'firebase-creation' && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                  Turn on
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFirebaseCreation('disabled')}
                  title={status.canDisableFirebaseCreation ? undefined : 'Metadata must be core first'}
                  disabled={!status.canDisableFirebaseCreation || status.firebaseAccountCreation !== 'enabled' || busy !== null}
                >
                  {busy === 'firebase-creation' && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                  Turn off
                </Button>
              )}
            </div>

            <details className="border-t pt-3">
              <summary className="text-xs font-medium cursor-pointer">Roll back to Firebase</summary>
              <div className="pt-3 space-y-2">
                <div className="text-xs text-muted-foreground">
                  In this order: metadata first (takes effect immediately), then env, then deploy.
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRollbackMetadata}
                    disabled={mode.metadataMode !== 'core' || busy !== null}
                  >
                    {busy === 'metadata-firebase' && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                    1. Metadata → firebase
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRollbackEnv}
                    disabled={envIsFirebase || busy !== null}
                  >
                    {busy === 'env-firebase' && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                    2. Env → firebase
                  </Button>
                  <Button variant="outline" size="sm" onClick={onDeployNow}>
                    3. Go to Deploy
                  </Button>
                </div>
              </div>
            </details>

            {actionNotice && <div className="text-xs text-green-600">{actionNotice}</div>}
            {actionError && <div className="text-xs text-destructive">{actionError}</div>}
          </>
        )}
      </CardContent>
    </Card>
  )
}
